import type { ReactNode } from 'react'
import styles from './Dialog.module.css'

interface DialogProps {
  title: string
  tone: 'warn' | 'danger'
  /** The serial or code the dialog is about, rendered LTR monospace. */
  subject?: string
  children: ReactNode
  /** Queue triage needs more than the standard cancel/confirm pair. */
  customActions?: ReactNode
  cancelLabel?: string
  confirmLabel?: string
  onCancel?: () => void
  onConfirm?: () => void
}

/**
 * Dialogs sit inside the app shell rather than in a portal, because the shell is the
 * device frame and the scrim must not cover the OS status bar area.
 */
export function Dialog({
  title,
  tone,
  subject,
  children,
  customActions,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
}: DialogProps) {
  return (
    <div className={styles.scrim} role="dialog" aria-modal="true" aria-label={title}>
      <div className={`${styles.dialog} ${tone === 'danger' ? styles.danger : ''}`}>
        <div
          className={`${styles.title} ${tone === 'danger' ? styles.titleDanger : styles.titleWarn}`}
        >
          {title}
        </div>

        {subject !== undefined && <div className={styles.subject}>{subject}</div>}

        <div className={`${styles.body} ${subject === undefined ? styles.bodySpaced : ''}`}>
          {children}
        </div>

        {customActions ?? (
          <div className={styles.actions}>
            <button
              type="button"
              className={`${styles.action} ${styles.cancel}`}
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              className={`${styles.action} ${styles.confirm}`}
              onClick={onConfirm}
            >
              {confirmLabel}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
